<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue';
import { useTinyMCEStore } from '@/stores/useTinyMCEStore';
import { useAppStore } from '@/stores/useAppStore';
import Editor from '@tinymce/tinymce-vue';

const appStore = useAppStore();
const tinyMCEStore = useTinyMCEStore();
const editorContent = ref<string>('');
const editorInitialized = ref<boolean>(false);

// Strip tags and attributes function
const stripTagsAndAttributes = (html: string): string => {
  const divSectionRegex = /<\/?(div|section|i|span)[^>]*>/gi;
  const tags = html.match(/(<\/?[\S][^>]*>)/gi);

  // Remove specific tags (div and section)
  html = html.replace(divSectionRegex, '');

  // Process attributes for remaining tags
  if (tags) {
    tags.forEach((tag) => {
      // Remove "class" attributes
      let noClass = tag.replace(/(class=".*?")|(class='.*?')|(class=[^\s>]*)/gi, '');

      // Remove "id" attributes
      let noID = noClass.replace(/(id=".*?")|(id='.*?')|(id=[^\s>]*)/gi, '');

      // Remove "data-" attributes
      let noData = noID.replace(/(data-.+?=".*?")|(data-.+?='.*?')|(data-[a-zA-Z0-9-]+)/gi, '');

      // Remove "style" attributes
      let noStyle = noData.replace(/(style=".*?")|(style='.*?')|(style=[^\s>]*)/gi, '');

      // Update html string
      html = html.replace(tag, noStyle);
    });
  }

  return html;
};

// TinyMCE configuration
const editorConfig = computed(() => ({
  height: 500,
  menubar: 'edit view insert format table tools',
  plugins: [
    'code',
    'link',
    'lists',
    'anchor',
    'table'
  ],
  toolbar: 'undo redo | formatselect | boldtag strongtag italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist | link | table | code',
  menu: {
    edit: { title: 'Edit', items: 'undo redo | cut copy paste | selectall' },
    insert: { title: 'Insert', items: 'link media template codesample inserttable' },
    format: {
      title: 'Format',
      items: 'bold italic underline strikethrough | blocks | align | removeformat',
    },
    tools: { title: 'Tools', items: 'spellchecker code' },
    table: { title: 'Table', items: 'inserttable tableprops deletetable | cell row column' },
  },

  // Allow all HTML tags and attributes
  valid_elements: '*[*]',
  extended_valid_elements: 'a[href|target=_blank]',

  // Paste settings - allow HTML but clean unnecessary attributes
  paste_as_text: false,
  paste_preprocess: (editor: any, args: any) => {
    args.content = stripTagsAndAttributes(args.content);
  },

  // Custom formats
  formats: {
    boldtag: { inline: 'b', remove: false },
    strongtag: { inline: 'strong', remove: false },
    italic: { inline: 'i', remove: false },
    underline: { inline: 'u', remove: false },
    strikethrough: { inline: 's', remove: false }
  },

  // Z-index fix for dialogs inside modal - TinyMCE dialogs should be above Vuetify modal
  base_z_index: 10000,

  // Setup custom buttons
  setup: (editor: any) => {
    // <b> button
    editor.ui.registry.addToggleButton('boldtag', {
      text: 'B',
      tooltip: 'Bold (B)',
      onAction: () => editor.execCommand('mceToggleFormat', false, 'boldtag'),
      onSetup: (buttonApi: any) => {
        editor.formatter.formatChanged('boldtag', (state: boolean) => {
          buttonApi.setActive(state);
        });
      }
    });

    // <strong> button
    editor.ui.registry.addToggleButton('strongtag', {
      text: 'Strong',
      tooltip: 'Bold (Strong)',
      onAction: () => editor.execCommand('mceToggleFormat', false, 'strongtag'),
      onSetup: (buttonApi: any) => {
        editor.formatter.formatChanged('strongtag', (state: boolean) => {
          buttonApi.setActive(state);
        });
      }
    });
  },

  // Enable code editing
  code_dialog_height: 600,
  code_dialog_width: 1000,

  // Status bar
  statusbar: true,
  elementpath: true,
  branding: false
}));

// Initialize editor on mount if API key is available
onMounted(() => {
  if (appStore.tinymce_api_key && !editorInitialized.value) {
    editorInitialized.value = true;
    console.log('TinyMCE Editor initialized on mount');
  }
});

// Initialize editor once when API key is available
watch(() => appStore.tinymce_api_key, (apiKey) => {
  if (apiKey && !editorInitialized.value) {
    editorInitialized.value = true;
    console.log('TinyMCE Editor initialized via watch');
  }
}, { immediate: true });

// Watch for modal open and sync content
watch(() => tinyMCEStore.isModalOpen, (isOpen) => {
  if (isOpen) {
    editorContent.value = tinyMCEStore.currentContent;
  }
});

// Watch for content changes in the store (when switching between fields)
watch(() => tinyMCEStore.currentContent, (newContent) => {
  if (tinyMCEStore.isModalOpen) {
    editorContent.value = newContent;
  }
});

const handleSave = () => {
  tinyMCEStore.saveContent(editorContent.value);
  tinyMCEStore.closeEditor();
};

const handleCancel = () => {
  tinyMCEStore.closeEditor();
};

const handleEditorInit = () => {
  tinyMCEStore.setEditorReady(true);
};
</script>

<template>
  <v-dialog
    v-model="tinyMCEStore.isModalOpen"
    max-width="1200px"
    persistent
    scrollable
    eager
    :retain-focus="false"
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Edit Content</span>
        <v-btn
          icon="$close"
          variant="text"
          @click="handleCancel"
        ></v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text style="min-height: 600px; padding: 20px;">
        <div v-if="editorInitialized">
          <Editor
            :api-key="appStore.tinymce_api_key"
            v-model="editorContent"
            :init="editorConfig"
            @init="handleEditorInit"
          />
        </div>
        <v-alert v-else type="warning" variant="tonal" class="mt-4">
          Initializing TinyMCE Editor...
        </v-alert>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          variant="text"
          @click="handleCancel"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          @click="handleSave"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.v-card-text {
  background-color: #f5f5f5;
}
</style>

<style>
/* Fix TinyMCE dialogs z-index to appear above Vuetify modals */
.tox-tinymce-aux {
  z-index: 10000 !important;
}

.tox.tox-silver-sink.tox-tinymce-aux {
  z-index: 10000 !important;
}

/* Ensure TinyMCE dialogs are above everything */
.tox .tox-dialog-wrap {
  z-index: 10001 !important;
}

.tox .tox-dialog {
  z-index: 10001 !important;
}

/* TinyMCE modal backdrop */
.tox-dialog__backdrop {
  z-index: 10000 !important;
}

/* Fix TinyMCE dropdown menus (select, listbox, etc.) */
.tox .tox-collection {
  z-index: 10002 !important;
}

.tox .tox-menu {
  z-index: 10002 !important;
}

.tox .tox-listbox {
  z-index: 10002 !important;
}

.tox .tox-pop {
  z-index: 10002 !important;
}

/* TinyMCE toolbar dropdowns */
.tox .tox-tbtn--select {
  z-index: 10002 !important;
}

.tox .tox-collection--list {
  z-index: 10002 !important;
}

.tox .tox-collection--toolbar {
  z-index: 10002 !important;
}

/* TinyMCE color picker */
.tox .tox-swatches {
  z-index: 10002 !important;
}

/* Enable pointer events and focus on TinyMCE dialogs and inputs */
.tox .tox-dialog__body-content,
.tox .tox-dialog input,
.tox .tox-dialog textarea,
.tox .tox-dialog select,
.tox .tox-textfield,
.tox .tox-textarea {
  pointer-events: auto !important;
}

/* Ensure TinyMCE dialogs can receive focus */
.tox-dialog input:focus,
.tox-dialog textarea:focus,
.tox-dialog select:focus {
  outline: auto !important;
}
</style>
